import { FC, useState } from 'react';
import styled from 'styled-components';

import { ReactComponent as GearIcon } from '@/assets/icons/gear-icon.svg';
import useAlerts from '@/hooks/useAlerts';
import { ExportPresetResource } from '@/types/resources';
import { download } from '@/utils/helpers';

import { Footer, ScreenHeader, Screens } from '.';
import Button from '../Button';
import Text from '../Text';
import ColumnsSelection from './ColumnsSelection';

const HeaderButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const StyledButton = styled(Button)`
  color: ${({ theme }) => theme.colors.black};
  svg {
    margin-right: 8px;
  }
  &:hover {
    border-color: ${({ theme }) => theme.colors.gray};
  }
  &:disabled {
    color: ${({ theme }) => theme.colors.gray};
    border-color: ${({ theme }) => theme.colors.gray};
    cursor: not-allowed;
  }
`;

const PresetsButton = styled(StyledButton)`
  svg {
    margin-right: 0;
  }
`;

const PresetsWrap = styled.div`
  position: relative;
`;

const PresetsDropdown = styled.div`
  padding: 4px;
  background: ${({ theme }) => theme.colors.white};
  border-radius: 4px;
  position: absolute;
  right: 0;
  top: calc(100% + 8px);
  z-index: 2;
  width: 172px;
  box-shadow: 0px 0px 8px rgba(37, 40, 43, 0.12);
`;

const PresetButton = styled(Text)`
  padding: 12px 10px;
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.gray};
  width: 100%;
  text-align: left;

  &:hover {
    color: ${({ theme }) => theme.colors.black};
    background: ${({ theme }) => theme.colors.grayscaleLight};
  }
`;

interface Props {
  onCancel: () => void;
  fileName: string;
  onExport: (cols: string[]) => Promise<string>;
  setCurrentScreen: React.Dispatch<React.SetStateAction<Screens>>;
  columns: string[];
  exportables: string[];
  setExportables: React.Dispatch<React.SetStateAction<string[]>>;
  presets: ExportPresetResource[];
}

const ExportScreen: FC<Props> = ({
  onCancel,
  onExport,
  fileName,
  setCurrentScreen,
  columns,
  exportables,
  setExportables,
  presets,
}) => {
  const [isExporting, setIsExporting] = useState(false);
  const [showPresetsDropdown, setShowPresetsDropdown] = useState(false);

  const alerts = useAlerts();

  const toggleColumn = (col: string) => {
    setExportables((prev) => {
      if (prev.indexOf(col) === -1) {
        return [...prev, col];
      }

      return prev.filter((c) => c !== col);
    });
  };

  const onExportClick = async () => {
    try {
      setIsExporting(true);

      const csv = await onExport(exportables);

      download(csv, fileName);

      setIsExporting(false);
    } catch (_) {
      alerts.error();
      setIsExporting(false);
    }
  };

  return (
    <>
      <ScreenHeader>
        <Text $type="caption">Select metrics:</Text>
        <HeaderButtons>
          <StyledButton
            small
            color="action"
            onClick={() => setCurrentScreen(Screens.PRESET_FORM)}
          >
            <svg
              width="14"
              height="15"
              viewBox="0 0 14 15"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3 7.5H11"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M7 3.5L7 11.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Create preset
          </StyledButton>
          <PresetsWrap>
            <PresetsButton
              small
              color="action"
              onClick={() => setShowPresetsDropdown((prev) => !prev)}
            >
              Presets
              <svg
                width="14"
                height="15"
                viewBox="0 0 14 15"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M2.33203 5.75L6.83203 10.4167L11.332 5.75"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </PresetsButton>
            {showPresetsDropdown && (
              <PresetsDropdown>
                {presets.map((preset) => (
                  <PresetButton
                    as="button"
                    key={preset.id}
                    type="button"
                    $type="smallHeadline"
                    onClick={() => {
                      setExportables(preset.exportables);
                      setShowPresetsDropdown(false);
                    }}
                  >
                    {preset.name}
                  </PresetButton>
                ))}
              </PresetsDropdown>
            )}
          </PresetsWrap>
          <StyledButton
            small
            color="action"
            onClick={() => setCurrentScreen(Screens.PRESETS_MANAGEMENT)}
          >
            <GearIcon />
            Manage presets
          </StyledButton>
        </HeaderButtons>
      </ScreenHeader>
      <ColumnsSelection
        toggleColumn={toggleColumn}
        onColumnsReorder={setExportables}
        columns={columns}
        exportables={exportables}
      />
      <Footer>
        <Button type="button" onClick={onCancel} color="secondary">
          Cancel
        </Button>
        <Button
          type="button"
          onClick={onExportClick}
          loading={isExporting}
          disabled={isExporting}
        >
          Export CSV
        </Button>
      </Footer>
    </>
  );
};

export default ExportScreen;
